import { useState } from "react";
import { motion } from "framer-motion";
import { 
  Car, Plane, MapPin, Phone, Check, ArrowRight, Clock, Users,
  Shield, Star, Building, GraduationCap, Briefcase, Calendar
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const CMC_LOGO = "https://customer-assets.emergentagent.com/job_4c248649-c7ad-49ab-8962-bf67e4ed6b65/artifacts/33twacgu_IMG_0221.png";

// Shuttle services with flat rate pricing
const shuttleServices = [
  {
    id: "mgm-airport",
    name: "Montgomery Airport",
    description: "Montgomery Regional Airport (MGM)",
    price: 85,
    icon: Plane,
    type: "airport"
  },
  {
    id: "bhm-airport",
    name: "Birmingham Airport",
    description: "Birmingham-Shuttlesworth International (BHM)",
    price: 199,
    icon: Plane,
    type: "airport"
  },
  {
    id: "atl-airport",
    name: "Atlanta Airport",
    description: "Hartsfield-Jackson Atlanta International (ATL)",
    price: 299,
    icon: Plane,
    type: "airport"
  },
  {
    id: "hourly",
    name: "Hourly Service",
    description: "Hotel shuttles, base tours & more",
    price: 60,
    additionalHourPrice: 100,
    icon: Clock,
    type: "hourly",
    note: "First hour $60 | Additional hours $100/hr"
  }
];

const vehicleTypes = [
  {
    name: "Luxury Sedan",
    capacity: "1-3 passengers",
    description: "Executive sedan service for business travelers",
    icon: Car
  },
  {
    name: "Luxury SUV",
    capacity: "1-5 passengers",
    description: "Spacious SUV for families or small groups",
    icon: Car
  },
  {
    name: "Group Shuttle Van",
    capacity: "6-12 passengers",
    description: "Perfect for groups, teams, or events",
    icon: Users
  }
];

const militaryBases = [
  {
    name: "Maxwell Air Force Base",
    description: "Air University & 42nd Air Base Wing",
    services: ["Airport transfers", "PCS moves", "TDY transportation", "War College events"]
  },
  {
    name: "Gunter Annex",
    description: "Air Force Cyber College & Professional Education",
    services: ["Student transportation", "Conference shuttles", "Base to airport transfers"]
  }
];

const features = [
  { icon: Shield, title: "Licensed & Insured", description: "Fully licensed transportation service" },
  { icon: Star, title: "Military Friendly", description: "Proud to serve our armed forces" },
  { icon: Clock, title: "24/7 Availability", description: "Early flights & late arrivals welcome" },
  { icon: Users, title: "Professional Drivers", description: "Courteous, punctual & knowledgeable" }
];

const testimonials = [
  {
    name: "Capt. Michael R.",
    rating: 5,
    text: "Reliable service for my PCS move. Driver was on time and helped with all my luggage. Highly recommend!",
    role: "Maxwell AFB"
  },
  {
    name: "Col. Sarah T.",
    rating: 5,
    text: "Used their service for War College transportation. Professional and punctual every time.",
    role: "Air University"
  },
  {
    name: "TSgt. James W.",
    rating: 5,
    text: "Best airport shuttle in Montgomery! Fair prices and great service for military families.",
    role: "Gunter Annex"
  }
];

const ShuttleService = () => {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    name: "",
    email: "",
    phone: "",
    pickupDate: "",
    pickupTime: "",
    pickupLocation: "",
    dropoffLocation: "",
    passengers: "1",
    vehicleType: "",
    flightNumber: "",
    specialRequests: "",
    isMilitary: false
  });

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setShowBookingModal(true);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // For now, show success and we can integrate payment later
      toast.success("Booking request received! We'll contact you within 1 hour to confirm.");
      setShowBookingModal(false);
      setSelectedService(null);
      setBookingForm({
        name: "",
        email: "",
        phone: "",
        pickupDate: "",
        pickupTime: "",
        pickupLocation: "",
        dropoffLocation: "",
        passengers: "1",
        vehicleType: "",
        flightNumber: "",
        specialRequests: "",
        isMilitary: false
      });
    } catch (error) {
      toast.error("Failed to submit booking. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 via-obsidian to-obsidian"></div>
        <div className="container mx-auto max-w-6xl relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Logo */}
              <div className="relative w-20 h-20 mb-6">
                <div className="absolute inset-0 bg-gold/20 rounded-full blur-xl"></div>
                <img 
                  src={CMC_LOGO}
                  alt="Choice Mobile"
                  className="w-20 h-20 object-contain relative z-10 drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
                />
              </div>
              
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-4">
                <Shield className="w-4 h-4" />
                Military & Traveler Friendly
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                Premium
                <span className="block text-gold">Shuttle Service</span>
              </h1>
              
              <p className="text-white/70 text-lg mb-6">
                Reliable airport transfers and transportation for Maxwell AFB, Gunter Annex, 
                and the greater Montgomery area.
              </p>
              
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="px-3 py-1 bg-charcoal rounded-full text-white/80 text-sm border border-white/10">
                  <Plane className="w-4 h-4 inline mr-1" /> Airport Transfers
                </span>
                <span className="px-3 py-1 bg-charcoal rounded-full text-white/80 text-sm border border-white/10">
                  <Building className="w-4 h-4 inline mr-1" /> Hotel Shuttles
                </span>
                <span className="px-3 py-1 bg-charcoal rounded-full text-white/80 text-sm border border-white/10">
                  <GraduationCap className="w-4 h-4 inline mr-1" /> War College Transport
                </span>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gold text-black hover:bg-gold-light rounded-full px-8 py-6 text-lg font-bold"
                  data-testid="book-shuttle-btn"
                >
                  Book Your Ride
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <a href="tel:8772955905">
                  <Button
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5 rounded-full px-8 py-6 text-lg w-full"
                  >
                    <Phone className="mr-2 w-5 h-5" />
                    (877) 295-5905 ext 2
                  </Button>
                </a>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {/* Pricing Preview Cards */}
              <div className="bg-charcoal/50 rounded-3xl border border-white/10 p-6">
                <h3 className="font-heading text-xl font-bold text-white mb-4 text-center">
                  Flat Rate Pricing
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-4 bg-obsidian rounded-xl">
                    <div className="flex items-center gap-3">
                      <Plane className="w-5 h-5 text-gold" />
                      <span className="text-white">Montgomery Airport</span>
                    </div>
                    <span className="text-gold font-bold text-xl">$85</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-obsidian rounded-xl">
                    <div className="flex items-center gap-3">
                      <Plane className="w-5 h-5 text-gold" />
                      <span className="text-white">Birmingham Airport</span>
                    </div>
                    <span className="text-gold font-bold text-xl">$199</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-obsidian rounded-xl">
                    <div className="flex items-center gap-3">
                      <Plane className="w-5 h-5 text-gold" />
                      <span className="text-white">Atlanta Airport</span>
                    </div>
                    <span className="text-gold font-bold text-xl">$299</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-obsidian rounded-xl border border-gold/30">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-gold" />
                      <div>
                        <span className="text-white block">Hourly Service</span>
                        <span className="text-white/50 text-xs">Additional hours $100/hr</span>
                      </div>
                    </div>
                    <span className="text-gold font-bold text-xl">$60<span className="text-sm">/hr</span></span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Military Bases Section */}
      <section className="py-16 px-6 bg-charcoal/50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              Proudly Serving Our Military
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Maxwell AFB & Gunter Annex
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Dedicated transportation services for military personnel, families, and visitors
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {militaryBases.map((base, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-obsidian rounded-2xl border border-blue-500/20 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white">{base.name}</h3>
                    <p className="text-white/60 text-sm">{base.description}</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {base.services.map((service, i) => (
                    <li key={i} className="flex items-center gap-2 text-white/80">
                      <Check className="w-4 h-4 text-gold" />
                      {service}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Flat rates with no hidden fees. Book online or call for a quote.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {shuttleServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-charcoal rounded-2xl border border-white/5 p-6 flex flex-col hover:border-gold/30 transition-colors"
              >
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center mb-4">
                  <service.icon className="w-7 h-7 text-gold" />
                </div>
                
                <h3 className="font-heading text-xl font-bold text-white mb-1">
                  {service.name}
                </h3>
                <p className="text-white/60 text-sm mb-4">{service.description}</p>
                
                <div className="mt-auto">
                  <p className="text-4xl font-bold text-gold mb-1">
                    ${service.price}
                    {service.type === "hourly" && <span className="text-lg">/hr</span>}
                  </p>
                  {service.note && (
                    <p className="text-white/50 text-xs mb-4">{service.note}</p>
                  )}
                  
                  <Button
                    onClick={() => handleServiceSelect(service)}
                    className="w-full bg-white/10 text-white hover:bg-gold hover:text-black rounded-full py-4 font-bold transition-colors"
                    data-testid={`book-${service.id}`}
                  >
                    Book Now
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Types */}
      <section className="py-16 px-6 bg-charcoal/50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Our Fleet
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Choose the perfect vehicle for your transportation needs
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {vehicleTypes.map((vehicle, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-obsidian rounded-2xl border border-white/5 p-6 text-center"
              >
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <vehicle.icon className="w-8 h-8 text-gold" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">{vehicle.name}</h3>
                <p className="text-gold font-medium mb-2">{vehicle.capacity}</p>
                <p className="text-white/60 text-sm">{vehicle.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-gold" />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-white/60 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-6 bg-charcoal/50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Trusted by Military Personnel
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-obsidian rounded-2xl border border-white/5 p-6"
              >
                <div className="flex mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-gold fill-gold" />
                  ))}
                </div>
                <p className="text-white/80 mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">{testimonial.name}</span>
                  <span className="text-blue-400 text-sm">{testimonial.role}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-900/30 to-gold/10 rounded-3xl border border-gold/30 p-8 md:p-12 text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Book Your Ride?
            </h2>
            <p className="text-white/70 max-w-xl mx-auto mb-8">
              Professional, reliable transportation for military personnel and travelers. 
              Book online or call us anytime.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                className="bg-gold text-black hover:bg-gold-light rounded-full px-10 py-6 text-lg font-bold"
              >
                Book Online
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <a href="tel:8772955905">
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 rounded-full px-10 py-6 text-lg w-full"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  (877) 295-5905 ext 2
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="bg-charcoal border-white/10 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-2xl">
              Book {selectedService?.name}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleBookingSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Your Name</Label>
                <Input
                  value={bookingForm.name}
                  onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                  required
                  className="bg-obsidian border-white/10 text-white mt-1"
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label className="text-white/70">Phone</Label>
                <Input
                  value={bookingForm.phone}
                  onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                  required
                  className="bg-obsidian border-white/10 text-white mt-1"
                  placeholder="(334) 555-1234"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white/70">Email</Label>
              <Input
                type="email"
                value={bookingForm.email}
                onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                required
                className="bg-obsidian border-white/10 text-white mt-1"
                placeholder="your@email.com"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Pickup Date</Label>
                <Input
                  type="date"
                  value={bookingForm.pickupDate}
                  onChange={(e) => setBookingForm({...bookingForm, pickupDate: e.target.value})}
                  required
                  className="bg-obsidian border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">Pickup Time</Label>
                <Input
                  type="time"
                  value={bookingForm.pickupTime}
                  onChange={(e) => setBookingForm({...bookingForm, pickupTime: e.target.value})}
                  required
                  className="bg-obsidian border-white/10 text-white mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white/70">Pickup Location</Label>
              <Input
                value={bookingForm.pickupLocation}
                onChange={(e) => setBookingForm({...bookingForm, pickupLocation: e.target.value})}
                required
                className="bg-obsidian border-white/10 text-white mt-1"
                placeholder="Address or location name"
              />
            </div>
            
            <div>
              <Label className="text-white/70">Drop-off Location</Label>
              <Input
                value={bookingForm.dropoffLocation}
                onChange={(e) => setBookingForm({...bookingForm, dropoffLocation: e.target.value})}
                required
                className="bg-obsidian border-white/10 text-white mt-1"
                placeholder="Address or airport code"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Passengers</Label>
                <Select
                  value={bookingForm.passengers}
                  onValueChange={(value) => setBookingForm({...bookingForm, passengers: value})}
                >
                  <SelectTrigger className="bg-obsidian border-white/10 text-white mt-1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                      <SelectItem key={n} value={String(n)} className="text-white">{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/70">Vehicle Type</Label>
                <Select
                  value={bookingForm.vehicleType}
                  onValueChange={(value) => setBookingForm({...bookingForm, vehicleType: value})}
                >
                  <SelectTrigger className="bg-obsidian border-white/10 text-white mt-1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    <SelectItem value="sedan" className="text-white">Luxury Sedan</SelectItem>
                    <SelectItem value="suv" className="text-white">Luxury SUV</SelectItem>
                    <SelectItem value="van" className="text-white">Group Shuttle Van</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {selectedService?.type === "airport" && (
              <div>
                <Label className="text-white/70">Flight Number (optional)</Label>
                <Input
                  value={bookingForm.flightNumber}
                  onChange={(e) => setBookingForm({...bookingForm, flightNumber: e.target.value})}
                  className="bg-obsidian border-white/10 text-white mt-1"
                  placeholder="e.g., AA1234"
                />
              </div>
            )}
            
            <div>
              <Label className="text-white/70">Special Requests</Label>
              <Textarea
                value={bookingForm.specialRequests}
                onChange={(e) => setBookingForm({...bookingForm, specialRequests: e.target.value})}
                className="bg-obsidian border-white/10 text-white mt-1"
                placeholder="Car seats, luggage assistance, etc."
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="military"
                checked={bookingForm.isMilitary}
                onChange={(e) => setBookingForm({...bookingForm, isMilitary: e.target.checked})}
                className="w-4 h-4"
              />
              <Label htmlFor="military" className="text-white/80 cursor-pointer">
                I am active duty military or a military family member
              </Label>
            </div>

            {/* Price Summary */}
            <div className="bg-obsidian rounded-xl p-4 border border-white/10">
              <div className="flex justify-between items-center">
                <span className="text-white/70">{selectedService?.name}</span>
                <span className="text-gold text-2xl font-bold">
                  ${selectedService?.price}
                  {selectedService?.type === "hourly" && "/hr"}
                </span>
              </div>
              {selectedService?.note && (
                <p className="text-white/50 text-sm mt-1">{selectedService.note}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gold text-black hover:bg-gold-light rounded-full py-6 font-bold"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Request Booking
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
            
            <p className="text-white/50 text-xs text-center">
              We'll confirm your booking within 1 hour. Payment collected at pickup.
            </p>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ShuttleService;
